import { useEffect, useState } from 'react';
import { Modal } from '../../../components/modal/modal';
import { Field } from '../../../components/field/field';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import { Spinner } from '../../../components/spinner/spinner';
import { useAdminFormState } from '../hooks/use-admin-form-state';
import { useAdminOperations } from '../hooks/use-admin-operations';
import { toast } from '../../../util/toasts';
import { PasswordRequirements } from '../../../clients/quipli-service/admin/admin';

interface CreateAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAdminModal = ({ isOpen, onClose, onSuccess }: CreateAdminModalProps) => {
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements | null>(null);
  const { createUser, fetchPasswordRequirements, isCreating } = useAdminOperations();

  const {
    // Form state
    adminUsername,
    adminFullName,
    adminEmail,
    errorMessage,
    isDisabled,

    // Form actions
    setUsername,
    setFullname,
    setEmail,

    // Validation functions
    checkConfirmPassword,
  } = useAdminFormState({
    type: 'create',
    passwordRequirements: passwordRequirements || undefined,
  });

  // Load password requirements on mount
  useEffect(() => {
    if (isOpen) {
      fetchPasswordRequirements().then(setPasswordRequirements);
    }
  }, [isOpen]);

  const handleCreateUser = async () => {
    if (!checkConfirmPassword()) {
      toast('Error password and confirm password should be the same', 'error');
      return;
    }

    const newAdmin = await createUser({
      adminUsername,
      adminEmail,
      adminFullName,
    });
    if (newAdmin) {
      resetForm();
      onSuccess();
      onClose();
    }
  };

  const resetForm = () => {
    setUsername('');
    setFullname('');
    setEmail('');
  };

  if (!isOpen) return null;

  return (
    <Modal title="Add new user" isOpen={isOpen} close={onClose}>
      <div className="w-full">
        <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)] mb-3">
          The user will receive an email with a link to set up their password.
        </Typography>
        <Field
          className="w-full mt-3"
          type="text"
          label="Full name"
          value={adminFullName}
          onChange={(fullname: string) => {
            setFullname(fullname);
          }}
          isRequired={true}
          dataCy="admin-fullname"
        />
        <Field
          className="w-full mt-3"
          type="text"
          label="Primary email"
          value={adminEmail}
          onChange={(email: string) => {
            setEmail(email);
          }}
          isRequired={true}
          dataCy="admin-email"
        />
        <Field
          className="w-full mt-3"
          type="text"
          label="User name"
          value={adminUsername}
          onChange={(username: string) => {
            setUsername(username);
          }}
          isRequired={true}
          dataCy="admin-username"
        />
        {errorMessage && <div className="text-[var(--error-500)]">{errorMessage}</div>}
      </div>

      <div className="dialog-actions !px-0 !pb-0">
        <div className="flex flex-col sm:flex-row actionsContainer">
          {isCreating ? (
            <Spinner />
          ) : (
            <>
              <Button
                className="button button-block button-secondary mr-2 mb-2"
                onClick={onClose}
                dataCy="admin-cancel-button"
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Cancel
                </Typography>
              </Button>

              <Button
                className="button button-yellow button-primary mb-2"
                onClick={handleCreateUser}
                disabled={isDisabled}
                dataCy="admin-add-button"
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Add
                </Typography>
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
